import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError } from 'axios'
import axios from 'axios'
import qs from 'qs'
import { TOAST_STR } from 'src/constant'
import type { ResErr } from 'src/types/common/api'

import { envUtils } from '@/utils/envUtils'
import { toastError } from '@/utils/toast'

import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from '../jwtInterceptors'
import type { IdentifyAxiosInstance, IdentifyAxiosRequestConfig } from './identifyAxios.type'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://identify.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://identify-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://identify-dev.${domainProvider.TARGET_DOMAIN}`
})()

export const identifyAxios: IdentifyAxiosInstance = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: (params) => {
    return qs.stringify(params, {
      arrayFormat: 'comma',
    })
  },
  headers: {
    'x-platform': 'web',
  },
})

identifyAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

identifyAxios.interceptors.response.use(
  (response) => response,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>
      const config = axiosError.config as IdentifyAxiosRequestConfig

      if (axiosError.response) {
        const { status } = axiosError.response

        if (status === 500 && !config.skipErrorToast) {
          toastError(TOAST_STR.errorMessage)
        }
      }

      return Promise.reject(axiosError)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return identifyAxios.request(originalRequest)
    },
  ),
)
